import { ApiError, type WidgetAuth } from './api.js';

/** Se renueva un minuto antes de que venza (REQUIREMENTS, Hito 3). */
const RENEW_BEFORE_MS = 60_000;

export const noSession = () =>
  new ApiError('NO_SESSION', 'Tu sesión en el sistema terminó. Volvé a iniciar sesión.');

/** `exp` del JWT en milisegundos, o 0 si no se puede leer (se renueva en el próximo pedido). */
function expiresAt(token: string): number {
  try {
    const payload = token.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(atob(payload)) as { exp?: unknown };
    return typeof exp === 'number' ? exp * 1000 : 0;
  } catch {
    return 0;
  }
}

/** De dónde sale el token de sesión del sistema. `null`: el usuario no tiene sesión. */
export type TokenSource = () => Promise<string | null>;

/** `data-token-url`: un endpoint del sistema que lee la sesión de sus cookies. */
export function tokenFromUrl(tokenUrl: string): TokenSource {
  return async () => {
    let res: Response;
    try {
      res = await fetch(tokenUrl, {
        credentials: 'include',
        headers: { accept: 'application/json' },
      });
    } catch {
      throw new ApiError('NETWORK', 'No se pudo conectar con el sistema. Probá de nuevo.');
    }
    if (res.status === 401 || res.status === 403) return null;
    let token: unknown;
    try {
      ({ token } = (await res.json()) as { token?: unknown });
    } catch {
      /* respuesta sin JSON */
    }
    if (!res.ok || typeof token !== 'string' || !token) {
      throw new ApiError('TOKEN_URL_FAILED', 'No se pudo obtener el acceso al asistente.');
    }
    return token;
  };
}

/** Cuánto se espera a que la página defina la función de `data-token-provider`. */
const PROVIDER_WAIT_MS = 5_000;

/** `window.Lupa.lauraToken` a partir de `"Lupa.lauraToken"`. */
function lookup(path: string): unknown {
  return path
    .split('.')
    .reduce<unknown>((obj, key) => (obj as Record<string, unknown> | undefined)?.[key], window);
}

/**
 * `data-token-provider`: una función de la página (por su nombre, ej. `getLauraToken`) que
 * devuelve el token de sesión del usuario (el mismo que el front ya manda a su API), o `null` si no
 * hay sesión. Si todavía no existe (se define después de cargar el widget), se la espera unos
 * segundos.
 */
export function tokenFromProvider(name: string, waitMs = PROVIDER_WAIT_MS): TokenSource {
  return async () => {
    const deadline = Date.now() + waitMs;
    let fn = lookup(name);
    while (typeof fn !== 'function' && Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 100));
      fn = lookup(name);
    }
    if (typeof fn !== 'function') {
      throw new ApiError(
        'TOKEN_PROVIDER_MISSING',
        `La página no definió la función ${name} (data-token-provider).`,
      );
    }
    let token: unknown;
    try {
      token = await (fn as () => unknown)();
    } catch {
      throw new ApiError('TOKEN_PROVIDER_FAILED', 'No se pudo obtener el acceso al asistente.');
    }
    if (token === null || token === undefined || token === '') return null;
    if (typeof token !== 'string') {
      throw new ApiError('TOKEN_PROVIDER_FAILED', 'No se pudo obtener el acceso al asistente.');
    }
    return token;
  };
}

/**
 * Autenticación con la sesión del usuario en su sistema (REQUIREMENTS 6.4): el token se pide al
 * sistema (`data-token-url` o `data-token-provider`), se guarda **solo en memoria** y se vuelve a
 * pedir antes de que venza (si es un JWT) o cuando la API responde 401. Cada pedido lleva también
 * el sistema (`data-tenant`): con eso Laura sabe dónde validar el token.
 */
export class TokenAuth implements WidgetAuth {
  private token?: string;
  private expires = 0;
  private pending?: Promise<string>;
  private readonly source: TokenSource;

  /** Una URL es `data-token-url`; una función, cualquier otra fuente. */
  constructor(
    source: string | TokenSource,
    private readonly tenant?: string,
  ) {
    this.source = typeof source === 'string' ? tokenFromUrl(source) : source;
  }

  async headers(): Promise<Record<string, string>> {
    const token = await this.current();
    return {
      authorization: `Bearer ${token}`,
      ...(this.tenant ? { 'x-aura-tenant': this.tenant } : {}),
    };
  }

  /** Descarta el token actual y pide otro (la API lo rechazó). */
  async refresh(): Promise<void> {
    this.token = undefined;
    await this.current();
  }

  private current(): Promise<string> {
    if (this.token && Date.now() < this.expires - RENEW_BEFORE_MS)
      return Promise.resolve(this.token);
    // Varios pedidos a la vez comparten una sola llamada al sistema.
    this.pending ??= this.fetchToken().finally(() => (this.pending = undefined));
    return this.pending;
  }

  private async fetchToken(): Promise<string> {
    const token = await this.source();
    if (!token) throw noSession();
    this.token = token;
    this.expires = expiresAt(token);
    return token;
  }
}
